import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import BeforeAfter from "../components/BeforeAfter";
import useReveal from "../components/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/projects/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [slug]);

  if (!p) return <main className="min-h-[80vh] flex items-center justify-center bg-bone pt-32"><p className="label">Loading project…</p></main>;

  return (
    <main data-testid="page-project" className="bg-bone">
      {/* Hero */}
      <section className="relative w-full min-h-[90vh] overflow-hidden">
        <img src={p.hero} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/45 via-ink/20 to-ink/70" />
        <div className="relative z-10 w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 pt-40 pb-20 min-h-[90vh] flex flex-col justify-end">
          <p className="label text-bone bg-ink/60 backdrop-blur-sm self-start px-3 py-1.5">{p.category} · {p.location} · {p.year}</p>
          <h1 className="display text-bone text-[14vw] md:text-[10rem] leading-[0.9] tracking-tighter mt-4">{p.title}</h1>
        </div>
      </section>

      {/* Summary + specs */}
      <section className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 py-24 md:py-32 grid grid-cols-12 gap-10">
        <p className="label col-span-12 md:col-span-2 reveal">Overview</p>
        <p className="display col-span-12 md:col-span-7 text-3xl md:text-5xl leading-[1.1] reveal">{p.summary}</p>
        <div className="col-span-12 md:col-span-3 reveal">
          <p className="label mb-4">Specifications</p>
          <dl className="space-y-3">
            {Object.entries(p.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-ink/15 pb-2">
                <dt className="label">{k}</dt>
                <dd className="font-mono text-sm">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Before / After */}
      <section className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 pb-24">
        <p className="label mb-6 reveal">Before · After</p>
        <div className="reveal">
          <BeforeAfter before={p.before} after={p.after} label={`${p.title} — Before / After`} />
        </div>
      </section>

      {/* Gallery */}
      <section className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 pb-24">
        <p className="label mb-6 reveal">Gallery</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {p.gallery.map((src, i) => (
            <div key={i} className={`reveal overflow-hidden ${i % 3 === 0 ? "md:col-span-8 aspect-[16/10]" : "md:col-span-4 aspect-[4/5]"}`}>
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1400ms]" />
            </div>
          ))}
        </div>
      </section>

      {/* 3D viewer placeholder */}
      <section className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 pb-32">
        <div className="reveal border border-ink/20 bg-ink text-bone aspect-[16/9] flex flex-col items-center justify-center grain relative">
          <p className="label text-bone/50">3D Walkthrough</p>
          <p className="display text-3xl md:text-5xl mt-3 text-center max-w-2xl">Interactive 3D model coming soon</p>
          <p className="mt-4 text-bone/60 text-sm max-w-md text-center">A Three.js / Spline viewer of the {p.title} model will live here. Hook a glb file once available.</p>
          <div className="absolute bottom-4 right-6 label text-bone/40 font-mono">virarc.3d · ready</div>
        </div>
      </section>

      <section className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 pb-32">
        <Link to="/#work" data-testid="back-to-work" className="display italic text-3xl border-b border-ink hover:text-rust hover:border-rust transition-colors pb-1">← back to all projects</Link>
      </section>
    </main>
  );
}
